import Button, { ActionButton, BUTTON_VARIANT } from '@bugbug/core/components/Button';
import Input from '@bugbug/core/components/Input';
import { useFormik } from 'formik';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { Group } from '@bugbug/core/types/groups';
import type { Test } from '@bugbug/core/types/tests';
import type { Maybe } from '@bugbug/core/types/utils';
import FormField from '~/components/FormField';
import { Header, Footer, Content, ErrorInfo } from '~/components/modals/Modal';
import ScreenSizeDropdown from '~/components/ScreenSizeDropdown';
import useModal from '~/hooks/useModal';
import { selectDefaultProfile } from '~/modules/profile/profile.selectors';
import { useAppSelector } from '~/modules/store';
import { useCreateTest } from '~/modules/tests/hooks/useCreateTest/useCreateTest';
import { useUpdateTest } from '~/modules/tests/hooks/useUpdateTest/useUpdateTest';
import { getExtractedErrorMessages, isInternalServerError } from '~/services/api/utils';

import { FORM_FIELD } from './EditTestModal.constants';
import { TestCreateSchema, TestEditSchema } from './EditTestModal.schema';
import { Form } from './EditTestModal.styled';

interface EditTestModalProps {
  className?: string;
  title?: string;
  id?: Test['id'];
  group?: {
    id: string;
    testId: string;
  };
  name?: Test['name'];
  profileId?: Test['runProfileId'];
  screenSizeType?: Test['screenSizeType'];
}

const EditTestModal = ({
  className,
  title,
  id,
  group,
  name,
  profileId,
  screenSizeType,
}: EditTestModalProps) => {
  const { t } = useTranslation();
  const modal = useModal();
  const defaultProfile = useAppSelector(selectDefaultProfile);
  const [createTest, createTestRequest] = useCreateTest();
  const [updateTest, updateTestRequest] = useUpdateTest();

  const isEdit = !!id;

  const labels = useMemo(() => {
    if (isEdit) {
      return {
        title: t('editTestModal.edit.title.edit', 'Edit test'),
        submitButton: t('editTestModal.edit.submitButton', 'Save test'),
        namePlaceholder: t('editTestModal.edit.name.placeholder', 'Type name here'),
      };
    }
    return {
      title: t('editTestModal.new.title', 'New test'),
      submitButton: t('editTestModal.new.submitButton', 'Create test'),
      nameDescription: t(
        'editTestModal.new.nameInput.description',
        'If you will not define the name it will be generated automatically.',
      ),
      namePlaceholder: t(
        'editTestModal.new.name.placeholder',
        'Type name here or just click ENTER button',
      ),
    };
  }, [t, isEdit]);

  const initialValues = useMemo<{
    testId?: Test['id'];
    groupId?: Group['id'];
    name: Maybe<Test['name']>;
    runProfileId: Test['runProfileId'];
    screenSizeType: Test['screenSizeType'];
  }>(
    () => ({
      groupId: group?.id,
      testId: group?.testId || id,
      name: name || null,
      runProfileId: profileId || defaultProfile?.id,
      screenSizeType: screenSizeType ?? 'desktop',
    }),
    [id, name, group, defaultProfile, profileId, screenSizeType],
  );

  const handleSubmit = useCallback(
    async (values, formik) => {
      formik.setSubmitting(true);

      try {
        if (isEdit) {
          await updateTest(values.testId, {
            name: values.name,
            screenSizeType: values.screenSizeType,
          });
        } else {
          await createTest({
            name: values.name,
            runProfileId: values.runProfileId,
            screenSizeType: values.screenSizeType,
            group: {
              id: values.groupId,
              testId: values.testId,
            },
          });
        }
      } catch (error) {
        const extractedErrors = getExtractedErrorMessages(error);
        if (extractedErrors) {
          formik.setErrors(extractedErrors);
        }
      } finally {
        formik.setSubmitting(false);
      }
    },
    [isEdit, updateTest, createTest],
  );

  const formik = useFormik({
    initialValues,
    validationSchema: isEdit ? TestEditSchema : TestCreateSchema,
    onSubmit: handleSubmit,
  });

  const hasInternalServerError = isInternalServerError(
    createTestRequest.error || updateTestRequest.error,
  );

  return (
    <Form className={className} data-testid="EditTestModal" onSubmit={formik.handleSubmit}>
      <Header>{title || labels.title}</Header>
      <Content>
        <FormField
          label={t('editTestModal.name.label', 'Name')}
          description={labels.nameDescription}
        >
          <Input
            {...formik.getFieldProps(FORM_FIELD.NAME)}
            data-hj-allow
            placeholder={labels.namePlaceholder}
            error={formik.touched.name && formik.errors.name}
            autoFocus
            fullWidth
          />
        </FormField>
        <FormField label={t('editTestModal.screenSizeType.label', 'Screen size')}>
          <ScreenSizeDropdown {...formik.getFieldProps(FORM_FIELD.SCREEN_SIZE)} />
        </FormField>
      </Content>
      <Footer>
        <ErrorInfo isVisible={hasInternalServerError} />
        <Button
          data-testid="EditTestModal.CancelButton"
          onClick={modal.hide}
          disabled={formik.isSubmitting}
        >
          {t('default.button.cancel')}
        </Button>
        <ActionButton
          data-testid="EditTestModal.SubmitButton"
          variant={BUTTON_VARIANT.PRIMARY}
          type="submit"
          pending={formik.isSubmitting}
        >
          {labels.submitButton}
        </ActionButton>
      </Footer>
    </Form>
  );
};

export default EditTestModal;
